import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { GENERATE_AUTH_TOKEN } from '../config';
import { Sparkles, Undo2, Redo2, Save, Loader2, Check, ArrowUp, X, Info } from 'lucide-react';

export const EditarVideo: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const videoIdParam = (params.get('id') || '').trim();
  const companyId = (params.get('companyId') || '').trim();
  const videoId: string = videoIdParam;

  const [loading, setLoading] = useState<boolean>(true);
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [instruction, setInstruction] = useState<string>('');
  const [history, setHistory] = useState<{ id: string; url: string }[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(-1);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showEditSuccess, setShowEditSuccess] = useState<boolean>(false);
  const [showInsufficientImagesModal, setShowInsufficientImagesModal] = useState<boolean>(false);
  const [remainingImagesCount, setRemainingImagesCount] = useState<number>(0);
  const desktopInputRef = useRef<HTMLTextAreaElement>(null);
  const hasEdits = currentIndex > 0;

  const checkSubscriptionLimits = async (): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!profile?.id_company) throw new Error('No company associated with user');

      const { data: companyProfile } = await supabase
        .from('company_profiles')
        .select('plan, imagenes_restantes')
        .eq('id_company', profile.id_company)
        .single();

      if (!companyProfile) throw new Error('No company profile found');

      const remaining = (companyProfile as any).imagenes_restantes as number | null | undefined;
      // Video editing requires 24 images
      if (typeof remaining === 'number' && remaining < 24) {
        setRemainingImagesCount(remaining);
        setShowInsufficientImagesModal(true);
        return false;
      }

      return true;
    } catch (err) {
      console.error('Error checking subscription limits:', err);
      return true;
    }
  };

  useEffect(() => {
    const fetchVideo = async () => {
      if (!videoIdParam || !companyId) {
        setLoading(false);
        setError('No se encontró información del video.');
        return;
      }
      try {
        setLoading(true);
        setError(null);
        let videoRow: { id: string; file_name: string } | null = null;
        const { data: byId, error: byIdErr } = await supabase
          .from('imagenes_generadas')
          .select('id, file_name')
          .eq('id', videoIdParam)
          .eq('id_company', companyId)
          .maybeSingle();
        if (byId && !byIdErr) videoRow = byId as any;
        if (!videoRow) {
          const { data: exactData } = await supabase
            .from('imagenes_generadas')
            .select('id, file_name')
            .eq('file_name', videoIdParam)
            .eq('id_company', companyId)
            .maybeSingle();
          if (exactData) videoRow = exactData as any;
        }
        if (!videoRow) {
          const { data: likeData } = await supabase
            .from('imagenes_generadas')
            .select('id, file_name')
            .like('file_name', `${videoIdParam}.%`)
            .eq('id_company', companyId)
            .maybeSingle();
          if (likeData) videoRow = likeData as any;
        }
        if (!videoRow) throw new Error('Video no disponible');
        if (!videoRow?.file_name) throw new Error('Video no disponible');

        const filePath = `${companyId}/full/${videoRow.file_name}`;
        const { data: signed } = await supabase.storage
          .from('generadas')
          .createSignedUrl(filePath, 3600);
        if (signed?.signedUrl) {
          setVideoUrl(signed.signedUrl);
          setHistory([{ id: videoRow.id as string, url: signed.signedUrl }]);
          setCurrentIndex(0);
        } else {
          throw new Error('No se pudo obtener el video');
        }
      } catch (e: any) {
        console.error('Error cargando video para edición:', e);
        setError('Error al cargar el video para edición.');
      } finally {
        setLoading(false);
      }
    };

    fetchVideo();
  }, [videoId, companyId]);

  useEffect(() => {
    if (currentIndex >= 0 && history[currentIndex]) {
      setVideoUrl(history[currentIndex].url);
    }
  }, [currentIndex, history]);

  useEffect(() => {
    if (!showEditSuccess) return;
    const t = window.setTimeout(() => setShowEditSuccess(false), 3000);
    return () => window.clearTimeout(t);
  }, [showEditSuccess]);

  const autoResizeTextarea = (el: HTMLTextAreaElement | null) => {
    if (!el) return;
    const baseHeight = 44;
    const text = el.value || '';

    if (!text) {
      el.style.height = `${baseHeight}px`;
      el.style.overflowY = 'hidden';
      return;
    }

    el.style.height = 'auto';
    const scroll = el.scrollHeight;

    const oneLineThreshold = baseHeight + 4;
    const twoLineThreshold = baseHeight * 2 + 4;

    let lines = 1;
    if (scroll > twoLineThreshold) {
      lines = 3;
    } else if (scroll > oneLineThreshold) {
      lines = 2;
    }

    const targetHeight = baseHeight * lines;
    el.style.height = `${targetHeight}px`;
    el.style.overflowY = lines === 3 && scroll > targetHeight ? 'auto' : 'hidden';
  };

  const handleSendInstruction = async () => {
    try {
      const trimmed = instruction.trim();
      if (!trimmed) return;
      if (!companyId) {
        setError('Falta el companyId para editar.');
        return;
      }

      const canGenerate = await checkSubscriptionLimits();
      if (!canGenerate) return;

      setIsSubmitting(true);
      setError(null);
      setShowEditSuccess(false);

      const { data: auth } = await supabase.auth.getUser();
      const user = auth?.user;
      if (!user) {
        setError('No se pudo identificar al usuario.');
        setIsSubmitting(false);
        return;
      }

      const currentVideoId = (currentIndex >= 0 && history[currentIndex]
        ? history[currentIndex].id
        : videoId);

      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/editar_vid', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
        body: JSON.stringify({ user_id: user.id, video_id: currentVideoId, instruction: trimmed })
      });

      if (!response.ok) {
        throw new Error('Error al enviar la instrucción');
      }

      const data = await response.json();
      const newVideoIdRaw: string | number | undefined = data?.video_generado_id ?? data?.edited_video_id ?? data?.video_id ?? data?.id;
      if (!newVideoIdRaw) {
        throw new Error('No se recibió el ID del video editado');
      }

      let newRow: { id: string; file_name: string } | null = null;
      const newIdStr = String(newVideoIdRaw);
      const { data: idMatch } = await supabase
        .from('imagenes_generadas')
        .select('id, file_name')
        .eq('id', newIdStr)
        .eq('id_company', companyId)
        .maybeSingle();
      if (idMatch) newRow = idMatch as any;
      if (!newRow) {
        const { data: exact } = await supabase
          .from('imagenes_generadas')
          .select('id, file_name')
          .eq('file_name', newIdStr)
          .eq('id_company', companyId)
          .maybeSingle();
        if (exact) newRow = exact as any;
      }
      if (!newRow) {
        const { data: likeData } = await supabase
          .from('imagenes_generadas')
          .select('id, file_name')
          .like('file_name', `${newIdStr}.%`)
          .eq('id_company', companyId)
          .maybeSingle();
        if (likeData) newRow = likeData as any;
      }
      if (!newRow?.file_name) {
        throw new Error('No se pudo cargar el nuevo video editado');
      }

      const newFilePath = `${companyId}/full/${newRow.file_name}`;
      const { data: newSigned } = await supabase.storage.from('generadas').createSignedUrl(newFilePath, 3600);
      if (!newSigned?.signedUrl) {
        throw new Error('No se pudo obtener la URL firmada del video editado');
      }

      setHistory((prev) => {
        const base = currentIndex >= 0 ? prev.slice(0, currentIndex + 1) : prev;
        return [...base, { id: newRow!.id as string, url: newSigned.signedUrl }];
      });
      setCurrentIndex((prev) => (prev >= 0 ? prev + 1 : 0));

      setInstruction('');
      setShowEditSuccess(true);
    } catch (e: any) {
      console.error('Error enviando instrucción de edición:', e);
      setError(e?.message || 'Ocurrió un error al editar el video.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUndo = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const handleRedo = () => {
    if (currentIndex < history.length - 1) setCurrentIndex(currentIndex + 1);
  };

  const persistActiveFlags = async () => {
    try {
      if (!companyId) return;
      const ids = Array.from(new Set(history.map((h) => h.id)));
      const activeId = (currentIndex >= 0 && history[currentIndex]
        ? history[currentIndex].id
        : videoId);
      if (ids.length === 0 || !activeId) return;

      const { error: deactErr } = await supabase
        .from('imagenes_generadas')
        .update({ active: false })
        .in('id', ids)
        .eq('id_company', companyId);
      if (deactErr) {
        console.error('Error desactivando videos:', deactErr);
      }

      const { error: actErr } = await supabase
        .from('imagenes_generadas')
        .update({ active: true })
        .eq('id', activeId)
        .eq('id_company', companyId);
      if (actErr) {
        console.error('Error activando el video actual:', actErr);
      }
    } catch (e) {
      console.error('Error guardando estados activos:', e);
    }
  };

  const handleSaveAndExit = async () => {
    try {
      setIsSaving(true);
      setLoading(true);
      await persistActiveFlags();
    } finally {
      setIsSaving(false);
      const st = (location.state as any) || {};
      if (st.returnToResults && st.resultsVideoId) {
        const activeId = currentIndex >= 0 && history[currentIndex]
          ? history[currentIndex].id
          : videoId;
        navigate('/redes-sociales/video-generado', {
          state: {
            videoId: activeId,
            companyId: st.resultsCompanyId,
          },
          replace: true,
        });
        return;
      }
      navigate(-1);
    }
  };

  const handleCancel = () => {
    const st = (location.state as any) || {};
    if (st.returnToResults && st.resultsVideoId) {
      setLoading(true);
      navigate('/redes-sociales/video-generado', {
        state: {
          videoId: st.resultsVideoId,
          companyId: st.resultsCompanyId,
        },
        replace: true,
      });
      return;
    }
    setLoading(true);
    navigate(-1);
  };

  return (
    <div className="px-4 pt-0 pb-0 w-full bg-white sm:bg-transparent min-h-[100dvh] md:min-h-screen">
      {/* Desktop layout */}
      <div className="hidden md:flex md:flex-row md:gap-4 md:items-start md:justify-center" style={{ height: 'calc(100vh - 8rem)' }}>
        {/* Video a la izquierda (desktop only) */}
        <div className="flex-shrink-0 flex items-center justify-center" style={{ height: 'calc(100vh - 8rem)' }}>
          {loading ? (
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          ) : videoUrl ? (
            <div className="relative flex items-center justify-center h-full">
              {(isSubmitting || showEditSuccess) && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full shadow-sm">
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                        <span className="text-base font-medium text-gray-700">Editando video</span>
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 text-green-600" />
                        <span className="text-base font-medium text-gray-700">Video editado</span>
                      </>
                    )}
                  </div>
                </div>
              )}
              <video 
                src={videoUrl} 
                controls
                playsInline
                style={{ height: 'calc(100vh - 8rem)', width: 'auto', maxWidth: 'calc(100vw - 500px)' }} 
                className="object-contain rounded-lg"
              >
                Tu navegador no soporta la reproducción de video.
              </video>
            </div>
          ) : (
            <div className="text-gray-500">Video no disponible</div>
          )}
        </div>

        {/* Container principal a la derecha (desktop only) */}
        <div className="flex-1 min-w-[33%] max-w-[50%] bg-white flex flex-col rounded-2xl border border-gray-200" style={{ height: 'calc(100vh - 8rem)' }}>
          {/* Header */}
          <div className="relative px-6 py-4 bg-white z-10 flex justify-center items-center shadow-sm rounded-t-2xl">
            <h2 className="text-lg font-semibold text-gray-900 text-center">Editar video</h2>
            <button
              type="button"
              onClick={handleCancel}
              className="absolute right-6 top-3 h-10 px-4 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-full font-normal shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancelar
            </button>
          </div>

          {/* Error banner */}
          {error && (
            <div className="mx-6 mt-4 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
              <p className="text-sm font-medium text-red-600">Error al editar el video</p>
              <p className="text-sm text-red-600 mt-1">No se consumieron imágenes.</p>
            </div>
          )}

          {/* Content */}
          {loading ? (
            <div className="flex-1 p-8 flex flex-col items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
              <h3 className="text-lg font-semibold text-gray-900">Cargando...</h3>
              <p className="text-gray-500 mt-2 text-sm">Este proceso puede tardar unos momentos.</p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col justify-end p-4">
              {/* Text field container */}
              <div className={`flex flex-col bg-gray-50 border border-gray-300 rounded-3xl ${isSubmitting ? 'shine-effect' : ''} focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500`}>
                <div className="relative min-h-[44px]">
                  <textarea
                    ref={desktopInputRef}
                    value={instruction}
                    onChange={(e) => {
                      setInstruction(e.target.value);
                      autoResizeTextarea(e.target);
                    }}
                    placeholder="Describí el cambio que querés hacer..."
                    disabled={isSubmitting}
                    rows={1}
                    className="w-full pl-10 pr-4 bg-transparent focus:outline-none border-none text-sm placeholder:text-gray-400 disabled:text-gray-500 disabled:cursor-not-allowed resize-none overflow-hidden whitespace-pre-wrap"
                    style={{
                      lineHeight: '1.25rem',
                      paddingTop: '0.75rem',
                      paddingBottom: '0.5rem',
                    }}
                  />
                  <span className="pointer-events-none absolute left-3 top-3 text-gray-400">
                    <Sparkles className="w-4 h-4" aria-hidden="true" />
                  </span>
                </div>
                <div className="flex justify-end px-2 pb-2">
                  <button
                    type="button"
                    onClick={handleSendInstruction}
                    disabled={isSubmitting || !instruction.trim()}
                    className="h-9 w-9 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white rounded-full font-normal shadow-sm inline-flex items-center justify-center"
                    aria-label="Enviar instrucción"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-900 mt-3 font-medium flex items-center gap-1.5">
                <Info className="w-4 h-4 text-blue-500 flex-shrink-0" />
                Una edición de video consume 24 imágenes.
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Se recomienda realizar cambios genéricos. Modificaciones de pequeños detalles pueden no realizarse correctamente.
              </p>

              {/* Botones debajo del text field - solo visibles después de al menos una edición */}
              {history.length > 1 && (
                <div className="flex items-center gap-2 mt-3">
                  <button
                    type="button"
                    onClick={handleUndo}
                    disabled={currentIndex <= 0}
                    className="flex-1 h-10 px-3 bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9] disabled:opacity-60 disabled:cursor-not-allowed rounded-full font-normal shadow-sm inline-flex items-center justify-center gap-2 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    aria-label="Deshacer"
                  >
                    <Undo2 className="w-4 h-4" />
                    Deshacer
                  </button>
                  <button
                    type="button"
                    onClick={handleRedo}
                    disabled={currentIndex >= history.length - 1}
                    className="flex-1 h-10 px-3 bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9] disabled:opacity-60 disabled:cursor-not-allowed rounded-full font-normal shadow-sm inline-flex items-center justify-center gap-2 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    aria-label="Rehacer"
                  >
                    <Redo2 className="w-4 h-4" />
                    Rehacer
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveAndExit}
                    disabled={isSaving || !hasEdits}
                    className="flex-1 h-10 px-3 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed text-white rounded-full font-normal shadow-sm inline-flex items-center justify-center gap-2 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <Save className="w-4 h-4" />
                    Guardar
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Mobile layout */}
      <div className="md:hidden fixed inset-0 bg-white flex flex-col z-40 overflow-hidden px-4 pt-4">
        {/* Header */}
        <div className="relative pb-4 bg-white z-10 flex justify-center items-center flex-shrink-0">
          <h2 className="text-sm font-semibold text-gray-900 text-center">Editar<br /> video</h2>
          <button
            type="button"
            onClick={handleCancel}
            className="absolute right-0 top-0 h-10 px-4 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-full font-normal shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancelar
          </button>
        </div>

        {/* Error banner */}
        {error && (
          <div className="mt-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm flex-shrink-0">{error}</div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex-1 flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
            <h3 className="text-lg font-semibold text-gray-900">Cargando...</h3>
            <p className="text-gray-500 mt-2 text-sm">Este proceso puede tardar unos momentos.</p>
          </div>
        ) : (
          <div className="flex-1 flex flex-col justify-center items-center overflow-hidden min-h-0" style={{ maxHeight: 'calc(100dvh - 270px)' }}>
            {/* Video */}
            <div className="relative flex items-center justify-center max-h-full w-full">
              {videoUrl ? (
                <>
                  {(isSubmitting || showEditSuccess) && (
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20">
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full shadow-sm">
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                            <span className="text-sm font-medium text-gray-700">Editando video</span>
                          </>
                        ) : (
                          <>
                            <Check className="w-4 h-4 text-green-600" />
                            <span className="text-sm font-medium text-gray-700">Video editado</span>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                  <video 
                    src={videoUrl} 
                    controls
                    playsInline
                    className="max-w-full max-h-full object-contain rounded-2xl"
                  >
                    Tu navegador no soporta la reproducción de video.
                  </video>
                </>
              ) : (
                <div className="text-gray-500">Video no disponible</div>
              )}
            </div>
          </div>
        )}

        {/* Controles (mobile) */}
        <div
          className="bg-white pt-2 px-4 space-y-2 border-t border-gray-200 fixed bottom-0 left-0 right-0 z-20"
              style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
            >
              <div className={`flex flex-col bg-gray-50 border border-gray-300 rounded-3xl ${isSubmitting ? 'shine-effect' : ''} focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500`}>
                <div className="relative min-h-[44px]">
                  <textarea
                    value={instruction}
                    onChange={(e) => {
                      setInstruction(e.target.value);
                      autoResizeTextarea(e.target);
                    }}
                    placeholder="Describí el cambio que querés hacer..."
                    disabled={isSubmitting}
                    rows={1}
                    className="w-full pl-10 pr-4 bg-transparent focus:outline-none border-none text-sm placeholder:text-gray-400 disabled:text-gray-500 disabled:cursor-not-allowed resize-none overflow-hidden whitespace-pre-wrap"
                    style={{
                      lineHeight: '1.25rem',
                      paddingTop: '0.75rem',
                      paddingBottom: '0.5rem',
                    }}
                  />
                  <span className="pointer-events-none absolute left-3 top-3 text-gray-400">
                    <Sparkles className="w-4 h-4" aria-hidden="true" />
                  </span>
                </div>
                <div className="flex justify-end px-2 pb-2">
                  <button
                    type="button"
                    onClick={handleSendInstruction}
                    disabled={isSubmitting || !instruction.trim()}
                    className="h-9 w-9 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white rounded-full font-normal shadow-sm inline-flex items-center justify-center"
                    aria-label="Enviar instrucción"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-900 mt-2 font-medium flex items-center gap-1.5">
                <Info className="w-4 h-4 text-blue-500 flex-shrink-0" />
                Una edición de video consume 24 imágenes.
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Se recomienda realizar cambios genéricos. Modificaciones de pequeños detalles pueden no realizarse correctamente.
              </p>
              {/* Botones solo visibles después de al menos una edición */}
              {history.length > 1 && (
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={handleUndo}
                    disabled={currentIndex <= 0}
                    className="h-10 px-2 bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9] disabled:opacity-60 disabled:cursor-not-allowed rounded-full font-normal shadow-sm inline-flex items-center justify-center gap-2 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <Undo2 className="w-4 h-4" />
                    <span className="text-sm">Deshacer</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleRedo}
                    disabled={currentIndex >= history.length - 1}
                    className="h-10 px-2 bg-[#F8FAFC] text-gray-700 border border-[#EEF2F7] hover:bg-[#F1F5F9] disabled:opacity-60 disabled:cursor-not-allowed rounded-full font-normal shadow-sm inline-flex items-center justify-center gap-2 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <Redo2 className="w-4 h-4" />
                    <span className="text-sm">Rehacer</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveAndExit}
                    disabled={isSaving || !hasEdits}
                    className="h-10 px-2 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed text-white rounded-full font-normal shadow-sm inline-flex items-center justify-center gap-2 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <Save className="w-4 h-4" />
                    <span className="text-sm">Guardar</span>
                  </button>
                </div>
              )}
        </div>
      </div>
      {/* Insufficient Images Modal */}
      {showInsufficientImagesModal && (
        <div className="fixed inset-0 bg-brand-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-lg p-6 relative text-center">
            <button
              type="button"
              onClick={() => setShowInsufficientImagesModal(false)}
              className="absolute top-3 right-3 p-2 text-gray-500 hover:text-gray-700 rounded-md focus:outline-none"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-100 flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-orange-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Imágenes insuficientes</h3>
            <p className="text-sm text-gray-600 mb-2">
              Para editar este video necesitás <span className="font-semibold">24 imágenes</span>, pero solo tenés <span className="font-semibold">{remainingImagesCount} disponibles</span>.
            </p>
            <p className="text-sm text-gray-600 mb-5">
              Mejorá tu plan para obtener más imágenes y poder editar videos.
            </p>
            <button
              type="button"
              onClick={() => {
                setShowInsufficientImagesModal(false);
                navigate('/subscription');
              }}
              className="inline-flex items-center justify-center w-full h-10 bg-blue-500 hover:bg-blue-600 text-white rounded-full text-sm font-medium"
            >
              Ver planes
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditarVideo;
